import { ROUTES, type RouteId, type RouteInput, type RouteResponse } from "@mailhand/core/api";
import { apiErrorSchema } from "@mailhand/core/schemas";

type BodyArg<Id extends RouteId> = RouteInput<Id> extends undefined ? { body?: undefined } : { body: RouteInput<Id> };

/** Path params fill the `{name}` segments of the route's path. */
export type CallArgs<Id extends RouteId> = {
  params?: Record<string, string>;
  query?: Record<string, string | number>;
} & BodyArg<Id>;

interface LooseArgs {
  params?: Record<string, string>;
  query?: Record<string, string | number>;
  body?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export interface ApiClientDeps {
  baseUrl: string;
  fetch: typeof fetch;
  getToken: () => string | null;
  onUnauthorized: () => void;
  newIdempotencyKey: () => string;
}

export interface ApiClient {
  /** Calls a route and parses the JSON answer with the route's response schema. */
  call<Id extends RouteId>(id: Id, args: CallArgs<Id>): Promise<RouteResponse<Id>>;
}

export function fillPath(path: string, params: Record<string, string>): string {
  return path.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(value);
  });
}

function queryString(query: Record<string, string | number>): string {
  const search = new URLSearchParams(Object.entries(query).map(([key, value]) => [key, String(value)]));
  const text = search.toString();
  return text === "" ? "" : `?${text}`;
}

async function errorFrom(response: Response): Promise<ApiError> {
  const text = await response.text();
  const parsed = apiErrorSchema.safeParse(parseJsonOrNull(text));
  if (parsed.success) return new ApiError(response.status, parsed.data.error.code, parsed.data.error.message);
  return new ApiError(response.status, "internal", `The request failed (${response.status}).`);
}

function parseJsonOrNull(text: string): unknown {
  if (text === "") return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function createApiClient(deps: ApiClientDeps): ApiClient {
  async function send(id: RouteId, args: LooseArgs): Promise<Response> {
    const spec = ROUTES[id];
    const url = `${deps.baseUrl}${fillPath(spec.path, args.params ?? {})}${queryString(args.query ?? {})}`;
    const headers = new Headers();
    const token = deps.getToken();
    if (token !== null) headers.set("authorization", `Bearer ${token}`);
    if (args.body !== undefined) headers.set("content-type", "application/json");
    if ("idempotent" in spec && spec.idempotent) headers.set("idempotency-key", deps.newIdempotencyKey());
    const init: RequestInit = { method: spec.method, headers };
    if (args.body !== undefined) init.body = JSON.stringify(args.body);
    const response = await deps.fetch(url, init);
    if (response.status === 401) deps.onUnauthorized();
    if (!response.ok) throw await errorFrom(response);
    return response;
  }

  // The overload is sound: the answer is parsed with ROUTES[id].response, the schema RouteResponse<Id> is inferred from.
  function call<Id extends RouteId>(id: Id, args: CallArgs<Id>): Promise<RouteResponse<Id>>;
  async function call(id: RouteId, args: LooseArgs): Promise<unknown> {
    const response = await send(id, args);
    const schema = ROUTES[id].response;
    if (schema === null) return undefined;
    return schema.parse(parseJsonOrNull(await response.text()));
  }

  return { call };
}
